import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  IntegrationInstructions as IntegrationIcon,
  Api as ApiIcon,
  Functions as FunctionsIcon,
  Settings as SettingsIcon,
  CloudSync as CloudSyncIcon,
  Code as CodeIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  Webhook as WebhookIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

/**
 * TechnicalIntegrationPanel Component
 * Manages MCP tools, function calls, and integrations for agentic crew workflows
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - Project ID
 * @param {Object} props.project - Project data
 * @param {Function} props.showNotification - Function to show notifications
 * @returns {React.ReactElement} TechnicalIntegrationPanel component
 */
const TechnicalIntegrationPanel = ({
  projectId,
  project,
  showNotification
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [mcpConnections, setMcpConnections] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(null);

  // Initialize mock data
  useEffect(() => {
    // Mock MCP connections
    setMcpConnections([
      {
        id: 'mcp-1',
        name: 'Claude Code MCP',
        status: 'connected',
        description: 'Code generation and execution capabilities',
        type: 'code',
        config: { endpoint: 'mcp://claude-code', version: '1.0.0' },
        lastSync: new Date(Date.now() - 300000),
        functions: ['generateCode', 'executeCode', 'validateSyntax']
      },
      {
        id: 'mcp-2', 
        name: 'File System MCP',
        status: 'connected',
        description: 'File operations and workspace management',
        type: 'storage',
        config: { endpoint: 'mcp://filesystem', version: '1.2.1' },
        lastSync: new Date(Date.now() - 120000),
        functions: ['readFile', 'writeFile', 'listDirectory', 'createDirectory']
      },
      {
        id: 'mcp-3',
        name: 'Task Management MCP',
        status: 'disconnected',
        description: 'Task orchestration and workflow management',
        type: 'orchestration',
        config: { endpoint: 'mcp://task-manager', version: '2.0.0' },
        lastSync: new Date(Date.now() - 3600000),
        functions: ['createTask', 'updateTask', 'assignTask', 'trackProgress']
      }
    ]);

    // Mock available tools
    setAvailableTools([
      {
        id: 'tool-1',
        name: 'Code Generator',
        description: 'Generate code based on specifications',
        category: 'Development',
        enabled: true,
        parameters: ['language', 'framework', 'requirements'],
        usage: 45
      },
      {
        id: 'tool-2',
        name: 'API Connector', 
        description: 'Connect to external APIs and services',
        category: 'Integration',
        enabled: true,
        parameters: ['url', 'method', 'headers', 'payload'],
        usage: 28
      },
      {
        id: 'tool-3',
        name: 'Data Processor',
        description: 'Process and transform data between formats',
        category: 'Data',
        enabled: false,
        parameters: ['inputFormat', 'outputFormat', 'transformRules'],
        usage: 12
      },
      {
        id: 'tool-4',
        name: 'Workflow Automator',
        description: 'Automate multi-step workflows',
        category: 'Automation',
        enabled: true,
        parameters: ['steps', 'conditions', 'triggers'],
        usage: 67
      }
    ]);

    // Mock integrations
    setIntegrations([
      {
        id: 'int-1',
        name: 'GitHub Integration',
        type: 'git',
        status: 'active',
        description: 'Repository management and code synchronization',
        config: { repository: 'user/repo', branch: 'main' },
        lastActivity: new Date(Date.now() - 1800000)
      },
      {
        id: 'int-2',
        name: 'Slack Notifications',
        type: 'communication',
        status: 'active', 
        description: 'Send notifications to Slack channels',
        config: { webhook: 'https://hooks.slack.com/...', channel: '#dev-updates' },
        lastActivity: new Date(Date.now() - 600000)
      },
      {
        id: 'int-3',
        name: 'Database Connection',
        type: 'database',
        status: 'error',
        description: 'Direct database access for data operations',
        config: { host: 'db.example.com', database: 'project_db' },
        lastActivity: new Date(Date.now() - 7200000)
      }
    ]);
  }, []);

  // Handle connection testing
  const handleTestConnection = useCallback(async (connectionId) => {
    setTestingConnection(connectionId);
    try {
      // Simulate connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Random success/failure for demo
      const success = Math.random() > 0.3;
      
      if (success) {
        setMcpConnections(prev => prev.map(conn => 
          conn.id === connectionId 
            ? { ...conn, status: 'connected', lastSync: new Date() }
            : conn
        ));
        showNotification({
          message: 'Connection test successful!',
          type: 'success'
        });
      } else {
        showNotification({
          message: 'Connection test failed. Please check configuration.',
          type: 'error'
        });
      }
    } catch (error) {
      showNotification({
        message: 'Connection test error',
        type: 'error'
      });
    } finally {
      setTestingConnection(null);
    }
  }, [showNotification]);

  // Handle tool toggle
  const handleToolToggle = useCallback((toolId) => {
    setAvailableTools(prev => prev.map(tool =>
      tool.id === toolId ? { ...tool, enabled: !tool.enabled } : tool
    ));
  }, []);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
      case 'active':
        return 'success';
      case 'disconnected':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'disconnected':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <SettingsIcon />;
    }
  };

  // Render MCP Connections tab
  const renderMCPTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">MCP Connections</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddDialog(true)}
        >
          Add Connection
        </Button>
      </Box>

      <Grid container spacing={3}>
        {mcpConnections.map((connection) => (
          <Grid item xs={12} md={6} key={connection.id}>
            <Card>
              <CardHeader
                avatar={
                  connection.type === 'code' ? <CodeIcon /> :
                  connection.type === 'storage' ? <StorageIcon /> :
                  <IntegrationIcon />
                }
                title={connection.name}
                subheader={connection.description}
                action={
                  <Chip 
                    label={connection.status}
                    color={getStatusColor(connection.status)}
                    size="small"
                  />
                }
              />
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Endpoint: {connection.config.endpoint}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Version: {connection.config.version}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Last Sync: {connection.lastSync.toLocaleTimeString()}
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Functions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {connection.functions.map((func) => (
                      <Chip key={func} label={func} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  onClick={() => handleTestConnection(connection.id)}
                  disabled={testingConnection === connection.id}
                  startIcon={testingConnection === connection.id ? <RefreshIcon /> : <PlayArrowIcon />}
                >
                  {testingConnection === connection.id ? 'Testing...' : 'Test'}
                </Button>
                <Button size="small" startIcon={<EditIcon />}>
                  Configure
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Render Tools tab
  const renderToolsTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Available Tools</Typography>
      
      <List>
        {availableTools.map((tool) => (
          <Card key={tool.id} sx={{ mb: 2 }}>
            <ListItem>
              <ListItemIcon>
                <FunctionsIcon />
              </ListItemIcon>
              <ListItemText
                primary={tool.name}
                secondary={
                  <Box>
                    <Typography variant="body2">{tool.description}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                      <Chip label={tool.category} size="small" />
                      <Typography variant="caption">
                        Usage: {tool.usage}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={tool.usage} 
                        sx={{ width: 100, height: 4 }}
                      />
                    </Box>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <FormControlLabel
                  control={
                    <Switch
                      checked={tool.enabled}
                      onChange={() => handleToolToggle(tool.id)}
                      color="primary"
                    />
                  }
                  label=""
                />
              </ListItemSecondaryAction>
            </ListItem>
          </Card>
        ))}
      </List>
    </Box>
  );

  // Render Integrations tab
  const renderIntegrationsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">External Integrations</Typography>
        <Button variant="outlined" startIcon={<AddIcon />}>
          Add Integration
        </Button>
      </Box>

      {integrations.map((integration) => (
        <Accordion key={integration.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {getStatusIcon(integration.status)}
              <Box sx={{ ml: 2, flexGrow: 1 }}>
                <Typography variant="subtitle1">{integration.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {integration.description}
                </Typography>
              </Box>
              <Chip 
                label={integration.status}
                color={getStatusColor(integration.status)}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Configuration:</Typography>
                {Object.entries(integration.config).map(([key, value]) => (
                  <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{key}:</strong> {value}
                  </Typography>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Status:</Typography>
                <Typography variant="body2">
                  Last Activity: {integration.lastActivity.toLocaleString()}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button size="small" startIcon={<PlayArrowIcon />}>
                    Test
                  </Button>
                  <Button size="small" startIcon={<EditIcon />}>
                    Edit
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />}>
                    Remove
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            color: '#1976d2',
            mb: 1,
            fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif"
          }}
        >
          🔧 Technical Integration
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage MCP tools, function calls, and external integrations for your agentic crew.
        </Typography>
      </Box>

      {/* Integration Status Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Integration Status:</strong> {mcpConnections.filter(c => c.status === 'connected').length} of {mcpConnections.length} MCP connections active. 
          {integrations.filter(i => i.status === 'active').length} external integrations running.
        </Typography>
      </Alert>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="MCP Connections" icon={<CloudSyncIcon />} />
          <Tab label="Tools & Functions" icon={<FunctionsIcon />} />
          <Tab label="Integrations" icon={<IntegrationIcon />} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && renderMCPTab()}
      {activeTab === 1 && renderToolsTab()}
      {activeTab === 2 && renderIntegrationsTab()}

      {/* Add Connection Dialog */}
      <Dialog 
        open={showAddDialog} 
        onClose={() => setShowAddDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New MCP Connection</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Connection Name"
            margin="normal"
            placeholder="Enter connection name"
          />
          <TextField
            fullWidth
            label="Endpoint URL"
            margin="normal"
            placeholder="mcp://service-name"
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            placeholder="Describe what this connection does"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setShowAddDialog(false)}>
            Add Connection
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

TechnicalIntegrationPanel.propTypes = {
  projectId: PropTypes.string.isRequired,
  project: PropTypes.object,
  showNotification: PropTypes.func
};

export default TechnicalIntegrationPanel;